import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Loader2, Plus, Tag, Users } from 'lucide-react';
import { getEmailApiBaseUrl } from '../../../utils/emailApi';
import { getEmailTagDisplayName } from '../../../utils/emailTagLabel';
import LoadingSpinner from '../../common/LoadingSpinner';
import {
  gymMarketingCardInner,
  gymMarketingMainCol,
  gymMarketingSideCol,
  gymMarketingSplitGrid,
  gymModalInput,
  gymModalLabel,
  gymSocialBtnPrimary,
  gymSocialBtnSecondary,
  gymSocialCard,
  gymSocialMutedText,
  gymSocialTitleText,
} from '../gymPageTheme';

export default function GymEmailTagsPanel({ api }) {
  const { t } = useTranslation();
  const emailApi = getEmailApiBaseUrl();
  const [loading, setLoading] = useState(true);
  const [tags, setTags] = useState([]);
  const [optInMembers, setOptInMembers] = useState([]);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [tagBusyKey, setTagBusyKey] = useState(null);

  const customTags = tags.filter((tag) => !tag.is_system);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tagsRes, membersRes] = await Promise.all([
        api.get('gym_email_tags.php?action=list', { baseURL: emailApi }),
        api.get('gym_email_tags.php?action=opt_in_members', { baseURL: emailApi }),
      ]);
      setTags(tagsRes.data.tags || []);
      setOptInMembers(membersRes.data.members || []);
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.email.tags.loadError'));
    } finally {
      setLoading(false);
    }
  }, [api, emailApi, t]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) {
      return;
    }
    setSaving(true);
    try {
      await api.post('gym_email_tags.php?action=create', { name }, { baseURL: emailApi });
      setNewName('');
      toast.success(t('gym.email.tags.created'));
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.email.tags.createError'));
    } finally {
      setSaving(false);
    }
  };

  const handleBackfill = async () => {
    try {
      const res = await api.post('gym_email_tags.php?action=backfill', {}, { baseURL: emailApi });
      toast.success(t('gym.email.tags.backfillDone', { count: res.data.synced_members ?? 0 }));
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.email.tags.backfillError'));
    }
  };

  const toggleMemberTag = async (memberId, tag, isAssigned) => {
    const busyKey = `${memberId}-${tag.id}`;
    setTagBusyKey(busyKey);
    try {
      if (isAssigned) {
        await api.post(
          'gym_email_tags.php?action=unassign',
          { user_id: memberId, tag_id: tag.id },
          { baseURL: emailApi }
        );
      } else {
        await api.post(
          'gym_email_tags.php?action=assign',
          { user_id: memberId, tag_id: tag.id },
          { baseURL: emailApi }
        );
      }
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.email.tags.assignError'));
    } finally {
      setTagBusyKey(null);
    }
  };

  const unassignedMembers = optInMembers.filter((m) => !m.has_custom_tags);
  const assignedMembers = optInMembers.filter((m) => m.has_custom_tags);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className={gymMarketingSplitGrid}>
      <div className={`${gymMarketingSideCol} xl:col-span-1 space-y-4`}>
        <div className={`${gymSocialCard} ${gymMarketingCardInner} space-y-4`}>
          <div>
            <h2 className={`text-lg font-semibold ${gymSocialTitleText}`}>{t('gym.email.tags.title')}</h2>
            <p className={`text-sm mt-2 leading-relaxed ${gymSocialMutedText}`}>{t('gym.email.tags.hint')}</p>
            <p className={`text-sm mt-2 leading-relaxed ${gymSocialMutedText}`}>{t('gym.email.tags.consentHint')}</p>
          </div>

          <form onSubmit={handleCreate} className="space-y-3">
            <label className="block">
              <span className={gymModalLabel}>{t('gym.email.tags.newName')}</span>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className={`mt-2 w-full ${gymModalInput}`}
                placeholder={t('gym.email.tags.newNamePlaceholder')}
                maxLength={120}
              />
            </label>
            <button type="submit" disabled={saving} className={`${gymSocialBtnPrimary} w-full justify-center`}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {t('gym.email.tags.add')}
            </button>
          </form>

          <button type="button" onClick={handleBackfill} className={`${gymSocialBtnSecondary} text-sm w-full justify-center`}>
            {t('gym.email.tags.backfill')}
          </button>
        </div>

        <ul className="space-y-2">
          {tags.map((tag) => (
            <li key={tag.id} className={`${gymSocialCard} ${gymMarketingCardInner} flex items-center gap-3`}>
              <Tag className="h-5 w-5 shrink-0 opacity-70" />
              <div className="min-w-0 flex-1">
                <p className={`font-medium truncate ${gymSocialTitleText}`}>
                  {getEmailTagDisplayName(tag, t)}
                </p>
                <p className={`text-xs mt-1 ${gymSocialMutedText}`}>
                  {t('gym.email.tags.recipients', {
                    optIn: tag.recipients_opt_in,
                    email: tag.recipients_with_email,
                  })}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className={`${gymMarketingMainCol} xl:col-span-2`}>
        <div className={`${gymSocialCard} ${gymMarketingCardInner}`}>
          <div className="flex items-center gap-2 mb-1">
            <Users className="h-5 w-5 opacity-70 shrink-0" />
            <h2 className={`text-lg font-semibold ${gymSocialTitleText}`}>
              {t('gym.email.tags.optInPoolTitle')}
            </h2>
          </div>
          <p className={`text-sm mb-5 leading-relaxed ${gymSocialMutedText}`}>{t('gym.email.tags.optInPoolHint')}</p>

        {optInMembers.length === 0 ? (
          <p className={`text-sm ${gymSocialMutedText}`}>{t('gym.email.tags.optInPoolEmpty')}</p>
        ) : customTags.length === 0 ? (
          <p className={`text-sm ${gymSocialMutedText}`}>{t('gym.email.tags.optInPoolNoTags')}</p>
        ) : (
          <div className="space-y-6">
            {unassignedMembers.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-200">
                  {t('gym.email.tags.unassignedSection', { count: unassignedMembers.length })}
                </p>
                <ul className="space-y-3">
                  {unassignedMembers.map((member) => (
                    <MemberTagRow
                      key={member.id}
                      member={member}
                      customTags={customTags}
                      tagBusyKey={tagBusyKey}
                      onToggle={toggleMemberTag}
                    />
                  ))}
                </ul>
              </div>
            )}

            {assignedMembers.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-white/55">
                  {t('gym.email.tags.assignedSection', { count: assignedMembers.length })}
                </p>
                <ul className="space-y-3">
                  {assignedMembers.map((member) => (
                    <MemberTagRow
                      key={member.id}
                      member={member}
                      customTags={customTags}
                      tagBusyKey={tagBusyKey}
                      onToggle={toggleMemberTag}
                    />
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

function MemberTagRow({ member, customTags, tagBusyKey, onToggle }) {
  const assignedIds = new Set((member.custom_tags || []).map((tag) => tag.id));

  return (
    <li className="rounded-lg border border-gray-200/90 bg-white/80 p-4 dark:border-white/10 dark:bg-gray-800/40">
      <div className="mb-2 min-w-0">
        <p className="font-medium text-gray-900 dark:text-white truncate">{member.name}</p>
        <p className="text-xs text-gray-500 dark:text-white/60 truncate">{member.email}</p>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {customTags.map((tag) => {
          const active = assignedIds.has(tag.id);
          const busy = tagBusyKey === `${member.id}-${tag.id}`;
          return (
            <button
              key={tag.id}
              type="button"
              disabled={busy}
              onClick={() => onToggle(member.id, tag, active)}
              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${
                active
                  ? 'border-indigo-400 bg-indigo-100 text-indigo-900 dark:border-[#cba5fa]/50 dark:bg-indigo-900/50 dark:text-indigo-100'
                  : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400 dark:border-white/20 dark:bg-gray-900/60 dark:text-white/75'
              }`}
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Tag className="h-3 w-3 opacity-70" />}
              {tag.name}
            </button>
          );
        })}
      </div>
    </li>
  );
}
